import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Box, Chip, Divider, Popover, Stack, Typography } from "@mui/material";

import BaseCalendar from "../../../../component/calendar/BaseCalendar";
import SearchField, { searchMatch } from "../../../../component/common/SearchField";
// 금액은 fmtKRWFull(전체 자릿수)로만 적는다 — fmtKRW 는 만/억 축약이라 "10만"처럼 보인다.
// 실제 송금액·통장 잔액을 확인하는 화면이라 원 단위까지 그대로 보여야 한다(고정비 달력과 같은 이유).
import { financeApi, fmtKRWFull } from "../../api/financeApi";
import { CASH_PLAN_STATUS, statusInfo, statusOptions, STATUS_ORDER } from "../../constants/cashPlanStatus";

const DATE = "YYYY-MM-DD";

/** 잔액 이월을 정확히 맞추기 위해 조회를 앞으로 더 당기는 개월 수 (load 주석 참고). */
const BALANCE_LOOKBACK_MONTHS = 2;

/** 상태 필터 칩 — 라벨·색은 constants/cashPlanStatus.js 단일 원천(SSOT)에서 가져온다. */
const STATUS_CHIPS = statusOptions();

/**
 * 기본으로 켜지는 상태 — 전부. 완료까지 포함해 그 달에 실제로 오간 돈을 통째로 본다.
 * 완료가 예정 합계에 섞여 숫자가 부풀 걱정은 없다 — 합계는 예정 계열만 세고 완료는 따로 적는다({@link totalsOf}).
 */
const DEFAULT_STATUSES = STATUS_ORDER;

/**
 * 구분별 색 — 고정비 보라 · 출금 빨강 · 출금홀딩 홀딩색 · 입금 초록. 화면 어디서나 같은 색을 쓴다
 * (필터 칩 = 범례, 섹션 머리말, 항목 줄, 상단 월 합계). 색이 화면마다 다르면 범례가 무의미해진다.
 *   · color : 머리말·칩 등 '강조'용 원색
 *   · text  : 항목 줄 본문용 — 원색 그대로 여러 줄을 쓰면 눈이 아파서 한 단계 짙고 차분한 톤을 쓴다
 *
 * <p><b>출금홀딩</b>은 보류된 출금을 따로 세운 칸이다. 예전엔 홀딩도 그냥 '출금' 섹션에 섞어 두고
 * 취소선만 그었는데, 홀딩은 합계·잔액 어디에도 안 잡히다 보니(→ {@link totalsOf}) 그 날 칸의 숫자와
 * 아무 관계 없는 줄이 출금 목록 한가운데 흐리게 끼어 있는 꼴이라 "홀딩 건이 안 보인다"는 말이 나왔다.
 * 색은 새로 정하지 않고 상태 SSOT(cashPlanStatus)의 홀딩색을 그대로 가져온다 — 홀딩 칩과 같은 색이라야
 * 상태 칩·구분 칩·항목 줄이 서로 같은 것을 가리킨다는 게 눈으로 이어진다.
 */
const KIND_CHIPS = [
    { key: "FIXED",   label: "고정비",   color: "#7c3aed", bg: "#f5f3ff", text: "#6d28d9" },
    { key: "EXPENSE", label: "출금",     color: "#dc2626", bg: "#fef2f2", text: "#b91c1c" },
    { key: "HOLD",    label: "출금홀딩", color: CASH_PLAN_STATUS.HOLD.color, bg: CASH_PLAN_STATUS.HOLD.bg, text: "#b91c1c" },
    { key: "INCOME",  label: "입금",     color: "#16a34a", bg: "#f0fdf4", text: "#15803d" },
];
const KIND = Object.fromEntries(KIND_CHIPS.map(k => [k.key, k]));

/**
 * 날짜 칸 안 섹션 — 고정비 → 출금 → 출금홀딩 → 입금.
 * 고정비 달력과 같은 구조(섹션 머리말 + 그 아래 '항목명 - 금액' 줄)를 쓴다. 두 달력을 오가며 보는
 * 화면이라 같은 모양이어야 읽는 법을 다시 배우지 않는다. 지출 예정을 보는 화면이므로 나가는 돈이
 * 위, 그중에서도 매달 반드시 나가는 고정비가 맨 위다. 홀딩은 '나갈지 미정'이라 확정 출금 뒤에 둔다.
 */
const SECTIONS = KIND_CHIPS;

/**
 * 항목의 구분.
 *
 * <p>홀딩(보류)을 먼저 가른다 — 고정비든 일반 출금이든 일단 홀딩이 걸리면 그 달 나갈 돈에서 빠지므로,
 * 성격(고정비/일반)보다 '나갈지 미정'이라는 사실이 먼저다.
 *
 * <p>수입은 홀딩보다 앞서 가른다. 입금에는 지급 순위 개념이 없어 홀딩을 걸 수 없고(INCOME_STATUS_VALUES),
 * 혹 예전 데이터에 홀딩 입금이 있더라도 '출금홀딩'으로 묶으면 들어올 돈이 나갈 돈 자리에 앉는다.
 *
 * <p>고정비 판정은 지출에만 적용한다 — 고정비 템플릿은 수입으로도 만들 수 있는데(고정 임대수입 등),
 * 그걸 '고정비'로 묶으면 입금이 출금 쪽에 섞여 그 날 나갈 돈이 부풀어 보인다.
 * 수입은 언제나 입금이고, 고정 여부는 상세에서 '고정비' 태그로만 알린다.
 */
const bucketOf = (it) =>
    it.flowType === "INCOME" ? "INCOME"
    : it.status === "HOLD"   ? "HOLD"
    : it.fixed              ? "FIXED"
    : "EXPENSE";

/** 배지 정렬 순서 = 섹션 순서(고정비 → 출금 → 출금홀딩 → 입금). 두 곳이 어긋나지 않게 한 곳에서 뽑는다. */
const KIND_ORDER = Object.fromEntries(KIND_CHIPS.map((k, i) => [k.key, i]));

/**
 * 하루 입출금 합계.
 *
 * <p><b>완료를 따로 세지 않는다.</b> 완료는 '이미 실행된' 상태일 뿐 그 날 오간 돈이라는 점은 예정과 같고,
 * 같은 거래가 두 번 잡히지도 않는다 — 확정되면 예정 행(cash_plan)은 지워지고 원장(ledger)으로 옮겨가므로
 * 한 건은 예정이든 완료든 <b>한 번만</b> 나온다. 그래서 입금/출금 총액은 예정·완료를 함께 더한
 * "그 날 실제로 오가는 돈"이다. 예정/완료 구분이 필요한 곳은 월 단위 계획뿐이라 상단 요약에만 덧붙인다.
 *
 * <p>예외는 <b>홀딩</b> 하나다. 자금이 모자라 보류한 건이라 나갈지 미정이므로 합계에 넣으면 홀딩을 건
 * 의미가 사라진다. 서버의 잔액 계산도 홀딩을 빼므로(includeHold=false) 같은 기준을 쓴다 — 금액에서
 * 빼고 건수만 알린다.
 *
 * <p>fixed 는 out 의 <b>부분집합</b>이다(출금 총액 안에 고정비가 얼마인지) — 따로 더하면 이중 계상.
 */
const totalsOf = (list) => {
    const t = {
        in: 0, out: 0,                      // 그 날 오가는 돈 (예정 + 완료, 홀딩 제외)
        inFixed: 0, outFixed: 0,            // 그중 고정비분 — in/out 의 부분집합이라 괄호로만 덧붙인다
        inPlan: 0, outPlan: 0,              // 그중 아직 안 나간 예정분 — 월 요약의 '예정' 표기용
        hold: 0, holdCount: 0,
    };
    for (const it of list) {
        if (it.status === "HOLD") { t.hold += it.amount; t.holdCount++; continue; }
        const pending = it.status !== "COMPLETED";
        if (it.flowType === "INCOME") {
            t.in += it.amount;
            if (it.fixed) t.inFixed += it.amount;
            if (pending)  t.inPlan  += it.amount;
        } else {
            t.out += it.amount;
            if (it.fixed) t.outFixed += it.amount;
            if (pending)  t.outPlan  += it.amount;
        }
    }
    return t;
};

/** 그 달의 실제 지급일 (payDay 가 말일을 넘으면 말일로 보정) — 서버 generateRecurring 과 같은 규칙. */
const payDayOf = (t, monthDay) => Math.min(t.payDay || 1, monthDay.daysInMonth());
/** 지급일이 템플릿 유효기간[startDate,endDate] 안인지 — null 이면 무기한. */
const inPeriod = (t, day) =>
    (!t.startDate || !day.isBefore(dayjs(t.startDate), "day")) &&
    (!t.endDate   || !day.isAfter(dayjs(t.endDate),   "day"));

/** 현금흐름표 행 → 달력 항목 */
const toItem = (r) => ({
    key: r.rowKey || String(r.id),
    date: r.scheduledDate,
    flowType: r.flowType,
    // 결제 유형이 '고정비'이거나 고정비 템플릿에서 생성된 행 → 고정비
    fixed: r.paymentType === "고정비" || r.source === "RECURRING",
    title: r.title || "(제목 없음)",
    amount: Number(r.amount || 0),
    status: r.status,
    source: r.source,
    sourceLabel: r.sourceLabel,
    vendorName: r.vendorName,
    category: r.category,
    deptName: r.deptName,
    note: r.note,
    authorName: r.authorName,
    overdue: r.overdue,
    preview: false,
});

/**
 * 자금 달력 — 고정비·입금·출금 예정과 일자별 예상 잔액을 한 달 달력으로 본다.
 *
 * <p><b>이 화면은 조회 전용이다.</b> 현금 흐름표와 같은 데이터(/finance/cashflow)를 쓰되 표가 아니라
 * 달력으로 그린다. 표는 "무엇이 얼마"를, 달력은 "언제 몰려 있나"를 보여준다 — 자금 담당이 실제로 묻는
 * 질문은 대개 후자(25일에 얼마가 한꺼번에 나가는지, 그날 잔액이 버티는지)라서 별도 화면으로 둔다.
 * 등록·수정·상태 변경은 하지 않는다. 같은 데이터를 여러 화면에서 고칠 수 있으면 어디서 뭘 바꿨는지
 * 추적이 안 되므로, 자금계획은 <b>현금 흐름표</b>에서, 고정비 템플릿은 <b>고정비 달력</b>에서만 손댄다.
 *
 * <p><b>합계는 성격별로 나눠 센다</b> — 예정 계열(예정·긴급·우선지급·지급확정)은 다 '아직 안 나간 돈'이라
 * 한 덩어리로 더하고, 이미 나간 완료분은 회색으로 따로, 보류된 홀딩은 금액에서 빼고 건수만 알린다
 * (자세한 이유는 {@link totalsOf}). 그래서 상태 칩을 켜고 꺼도 '이번 달 나갈 돈'이 출렁이지 않는다.
 *
 * <p><b>홀딩은 '출금홀딩' 칸에 따로 세운다</b> — 합계·잔액에서 빠지는 돈이라 출금 목록에 섞어 두면
 * 그 칸의 어떤 숫자와도 이어지지 않아 있으나 마나 한 줄이 된다. 구분 칩(=범례)·섹션 머리말·일간 요약
 * 한 줄을 모두 주어, 얼마가 보류돼 있고 그게 왜 출금 합계에 없는지를 칸 안에서 바로 읽게 한다.
 *
 * <p><b>예상 잔액</b>은 날짜 칸마다(그 날 마감)·주간 줄(주말)·상단 띠(월말)에 함께 보여주고, 잔액이
 * 마이너스가 되는 날은 칸을 통째로 붉게 칠한다. 상단의 '자금 부족 시작'을 누르면 그 날짜로 이동한다.
 */
export default function CashCalendarTab() {
    const [anchor, setAnchor]       = useState(() => dayjs());
    const [rows, setRows]           = useState([]);
    const [base, setBase]           = useState(null);       // 기준잔액(자금일보 최신 일마감) — 잔액 계산의 출발점
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUSES);
    const [kindFilter, setKindFilter]     = useState(KIND_CHIPS.map(k => k.key));
    const [search, setSearch]       = useState("");
    const [detail, setDetail]       = useState(null);       // { item, anchorEl } — 항목 상세 팝오버(조회 전용)
    const [flashDate, setFlashDate] = useState(null);       // '자금 부족 시작' 으로 이동한 날 — 잠깐 테두리 강조

    const dayRefs   = useRef({});   // "YYYY-MM-DD" → 날짜 칸 안 엘리먼트 (부족 시점으로 스크롤)
    const flashTimer = useRef(null);

    const monthStart = useMemo(() => anchor.startOf("month"), [anchor]);
    const monthEnd   = useMemo(() => anchor.endOf("month"),   [anchor]);

    const load = useCallback(() => {
        setLoading(true);
        const params = {
            // 표시는 이번 달만 하지만 조회는 앞으로 더 당겨 잡는다.
            //   잔액(runningBalance)은 기준잔액(일마감)에서 <b>예정일 순으로 누적</b>한 값이라, 기준일과
            //   이번 달 사이의 확정 실적이 조회에서 빠지면 그만큼 잔액이 어긋난다(예정 행은 하한이 없어
            //   자동 포함되지만, 확정 원장은 [from,to] 안의 것만 내려온다). 일마감은 매일 도는 작업이라
            //   두 달이면 충분히 덮는다. 이 구간의 행은 달력에 그릴 때 이번 달로 다시 잘라낸다.
            from: monthStart.subtract(BALANCE_LOOKBACK_MONTHS, "month").format(DATE),
            to:   monthEnd.format(DATE),
            includeCompleted: true,   // 완료도 받아 두고 화면에서 상태 칩으로 거른다(칩을 켤 때 재조회하지 않게)
            // 홀딩은 잔액에서 뺀다 — 이 화면의 합계 규칙(totalsOf)과 같은 기준이라야
            // "출금 합계는 홀딩을 뺐는데 잔액은 안 뺐다" 같은 어긋남이 생기지 않는다.
            includeHold: false,
            page: 0,
            // 서버 기본 페이지 크기가 50이라 그대로 두면 달 중반부터 통째로 비어 보인다.
            // 예정 행에는 과거 하한이 없어(지연분 포함) 한 달치보다 많이 내려오므로 넉넉히 잡는다.
            size: 5000,
        };
        Promise.all([
            financeApi.getCashFlow(params).catch(() => null),
            financeApi.getRecurringTemplates().catch(() => []),
        ])
            .then(([flow, tpls]) => {
                setRows(Array.isArray(flow?.rows) ? flow.rows : []);
                setBase(flow?.baseBalance || null);
                setTemplates(Array.isArray(tpls) ? tpls : []);
            })
            .finally(() => setLoading(false));
    }, [monthStart, monthEnd]);
    useEffect(() => { load(); }, [load]);

    // 달을 옮기면 이전 달에서 잡아 둔 강조·칸 참조는 의미가 없다(칸이 새로 그려진다).
    useEffect(() => {
        setFlashDate(null);
        dayRefs.current = {};
    }, [monthStart]);
    useEffect(() => () => clearTimeout(flashTimer.current), []);

    // ── 이 달에 그릴 항목 ───────────────────────────────────────────
    // 서버는 예정일 하한 없이(지연분 포함) + 잔액용으로 넓힌 구간까지 내려주므로 그릴 범위로 먼저 자른다.
    const monthItems = useMemo(() => {
        const from = monthStart.format(DATE), to = monthEnd.format(DATE);
        return rows
            .filter(r => r.scheduledDate && r.scheduledDate >= from && r.scheduledDate <= to)
            .map(toItem);
    }, [rows, monthStart, monthEnd]);

    /**
     * 아직 생성되지 않은 이번 달 고정비 (미리보기 행).
     *
     * <p>고정비는 '고정비 월 생성'을 돌려야 그 달 예정 행이 만들어진다. 그 전까지 달력이 비어 보이면
     * "이번 달은 고정비가 없다"고 오해하게 되므로, 템플릿에서 그 달 지급일을 계산해 흐린 점선으로 미리
     * 보여준다. (생성은 이 화면에서 하지 않는다 — 조회 전용. 고정비 달력·현금 흐름표에서 처리한다.)
     *
     * <p>생성 여부는 '그 달에 이 템플릿에서 나온 행이 있는지'로 본다. 생성된 행은 source=RECURRING +
     * sourceId=템플릿ID 를 그대로 들고 있다(완료돼 원장으로 옮겨가도 유지). 생성 후 예정일을 다른 달로
     * 옮긴 건은 여기서 다시 '미생성'으로 보이지만, 그 경우 실제로 이 달엔 나갈 돈이 없으므로 표시가 맞다.
     */
    const previewItems = useMemo(() => {
        // 달력 항목(toItem)엔 sourceId 를 담지 않으므로 원본 rows 에서 직접 모은다.
        const generated = new Set();
        const from = monthStart.format(DATE), to = monthEnd.format(DATE);
        rows.forEach(r => {
            if (r.source !== "RECURRING" || r.sourceId == null || !r.scheduledDate) return;
            if (r.scheduledDate < from || r.scheduledDate > to) return;
            generated.add(r.sourceId);
        });
        return templates
            .filter(t => t.active && !generated.has(t.id))
            .map(t => {
                const day = monthStart.date(payDayOf(t, monthStart));
                if (!inPeriod(t, day)) return null;      // 계약 시작 전·만료 후 달은 발생하지 않는다
                return {
                    key: `TPL:${t.id}`,
                    date: day.format(DATE),
                    flowType: t.flowType || "EXPENSE",
                    fixed: true,
                    title: t.title || "(제목 없음)",
                    amount: Number(t.amount || 0),
                    status: "SCHEDULED",                 // 생성되면 예정으로 만들어진다
                    source: "RECURRING",
                    sourceLabel: "고정비",
                    vendorName: t.vendorName,
                    category: t.category,
                    deptName: null,
                    note: t.note,
                    authorName: null,
                    overdue: false,
                    preview: true,                       // 아직 자금계획으로 만들어지지 않은 미리보기
                };
            })
            .filter(Boolean);
    }, [templates, rows, monthStart, monthEnd]);

    // ── 필터 적용 ──────────────────────────────────────────────────
    const visible = useMemo(() => {
        const all = [...monthItems, ...previewItems];
        return all.filter(it =>
            statusFilter.includes(it.status)
            && kindFilter.includes(bucketOf(it))
            && searchMatch(search, [it.title, it.vendorName, it.category, it.deptName, it.note, String(it.amount)])
        );
    }, [monthItems, previewItems, statusFilter, kindFilter, search]);

    /** 날짜별 묶음 — 셀·주간 합계가 같은 원본을 보게 해 화면의 숫자가 서로 어긋나지 않게 한다. */
    const byDate = useMemo(() => {
        const m = new Map();
        visible.forEach(it => {
            if (!m.has(it.date)) m.set(it.date, []);
            m.get(it.date).push(it);
        });
        // 구분(고정비→출금→입금) 먼저, 같은 구분 안에서는 금액 큰 순 — 그 날 제일 큰 돈이 위에 온다.
        m.forEach(list => list.sort((a, b) =>
            KIND_ORDER[bucketOf(a)] - KIND_ORDER[bucketOf(b)] || b.amount - a.amount));
        return m;
    }, [visible]);

    const month = totalsOf(visible);
    const monthNet = month.in - month.out;

    /**
     * 일자별 예상 잔액 — <b>서버가 계산한 runningBalance 를 그대로 쓴다(다시 계산하지 않는다)</b>.
     *
     * <p>잔액은 기준잔액(일마감)에서 예정일 순으로 누적한 값이라 조회 구간보다 넓은 맥락이 필요하고,
     * 화면 필터로 흔들리면 안 된다. 그래서 여기서 다시 더하지 않고 서버 값을 읽기만 한다
     * (현금흐름표 computeCashflow 는 '전 기간·전사' 전용이라 구간을 좁히는 화면에서 재사용 금지 —
     * 그 파일 주석에 명시돼 있다).
     *
     * <p>그 날 마지막 행의 runningBalance = 그 날 마감 예상 잔액이다. 홀딩·기준일 이전 완료처럼 잔액을
     * 움직이지 않는 행은 서버가 null 로 두므로 건너뛰고, 아무 일도 없는 날은 전날 잔액을 그대로 잇는다.
     *
     * <p><b>상태 칩과 무관하다</b> — 칩은 '무엇을 볼지'만 정한다. 완료 칩을 껐다고 이미 나간 돈이
     * 통장에 도로 들어오진 않으므로, 잔액까지 칩을 따라 움직이면 그건 예측이 아니라 착시다.
     */
    const balanceByDate = useMemo(() => {
        const from = monthStart.format(DATE);
        let carry = base?.amount ?? 0;
        const dayEnd = new Map();
        [...rows]
            .filter(r => r.scheduledDate && r.runningBalance != null)
            // 서버가 (예정일 → id) 순으로 정렬해 내려주고 Array#sort 는 안정 정렬이라,
            // 같은 날짜 안에서는 서버 순서가 유지된다 → 마지막으로 덮어쓴 값이 그 날 마감 잔액.
            .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate))
            .forEach(r => {
                if (r.scheduledDate < from) carry = r.runningBalance;   // 이번 달 시작 전까지의 이월분
                else dayEnd.set(r.scheduledDate, r.runningBalance);
            });
        const out = new Map();
        for (let i = 0; i < monthStart.daysInMonth(); i++) {
            const key = monthStart.add(i, "day").format(DATE);
            if (dayEnd.has(key)) carry = dayEnd.get(key);
            out.set(key, carry);
        }
        return out;
    }, [rows, base, monthStart]);

    /** 이번 달 안에서 잔액이 처음 마이너스가 되는 날 — 달력이 보여주는 잔액과 같은 값에서 뽑는다. */
    const shortfallDate = useMemo(() => {
        for (const [d, v] of balanceByDate) if (v < 0) return d;
        return null;
    }, [balanceByDate]);
    const endBalance = balanceByDate.get(monthEnd.format(DATE));

    /** '자금 부족 시작' 클릭 → 그 날짜 칸으로 스크롤 + 잠깐 테두리 강조 */
    const jumpToShortfall = () => {
        if (!shortfallDate) return;
        dayRefs.current[shortfallDate]?.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashDate(shortfallDate);
        clearTimeout(flashTimer.current);
        flashTimer.current = setTimeout(() => setFlashDate(null), 2600);
    };

    const toggle = (setter, list, value) =>
        setter(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);

    // ── 렌더 ───────────────────────────────────────────────────────
    /**
     * 항목 한 줄 — 고정비 달력과 같은 '항목명 - 금액' 텍스트 줄.
     *
     * <p>배지·색 덩어리를 쓰지 않는 이유는 고정비 달력과 같다 — 한 날짜에 여러 건이 겹치면 달력이
     * 색 덩어리가 되어 정작 '무엇이 언제 나가는지'가 안 읽힌다. 방향(출금/입금)은 섹션 머리말이 한 번만
     * 말해주고 각 줄은 담백하게 둔다. 다만 <b>긴급·홀딩</b>은 지급 순서를 바꾸는 신호라 예외로,
     * 긴급은 붉은 굵은 글씨, 홀딩은 취소선으로 한눈에 갈리게 한다.
     */
    const renderItem = (it) => {
        const urgent = it.status === "URGENT";
        const hold   = it.status === "HOLD";
        return (
            <Box key={it.key}
                onClick={(e) => { e.stopPropagation(); setDetail({ item: it, anchorEl: e.currentTarget }); }}
                title={`${statusInfo(it.status).label} · ${it.title} - ${fmtKRWFull(it.amount)}${it.vendorName ? ` (${it.vendorName})` : ""}`}
                sx={{
                    px: 0.6, py: 0.2, borderRadius: 0.8, cursor: "pointer",
                    fontSize: 10.5, lineHeight: 1.25,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    // 항목 줄은 기본 검정 계열로 담백하게 둔다 — 구분(고정비·출금·입금)은 섹션 머리말 색이
                    // 한 번만 말해주면 충분하고, 줄마다 색을 입히면 한 날짜에 여러 건이 겹칠 때 달력이
                    // 색 덩어리가 되어 정작 '무엇이 언제 나가는지'가 안 읽힌다(고정비 달력과 같은 판단).
                    // 긴급만 예외 — 지급 순서를 바꾸는 신호라 붉은 굵은 글씨로 도드라지게 한다.
                    fontWeight: urgent ? 800 : 600,
                    color: urgent ? "#b91c1c" : "#334155",
                    // 완료 건은 흐리게 하지 않는다 — 그 날 실제로 오간 돈이라 예정과 똑같이 읽혀야 한다.
                    // 흐린 건 아직 존재하지 않는 것(미생성 고정비)과 보류된 것(홀딩)뿐이다.
                    // 홀딩은 '출금홀딩' 섹션에 따로 서 있고 취소선까지 있어 흐림에 기댈 필요가 없다.
                    // 너무 흐리면(0.55) 좁은 칸에서 아예 없는 것처럼 보여 홀딩을 놓친다 — 읽히는 선까지만 낮춘다.
                    opacity: it.preview ? 0.5 : hold ? 0.8 : 1,
                    textDecoration: hold ? "line-through" : "none",
                    transition: "background-color .12s, color .12s",
                    "&:hover": { bgcolor: "#eff6ff", color: "#1d4ed8", opacity: 1 },
                }}>
                {it.title} - {fmtKRWFull(it.amount)}
            </Box>
        );
    };

    const renderDayBadges = (day, { isCurrentMonth }) => {
        if (!isCurrentMonth) return null;              // 인접 달 셀엔 표시 안 함(중복 방지)
        const key = day.format(DATE);
        const items = byDate.get(key) || [];
        const bal = balanceByDate.get(key);
        const t = totalsOf(items);
        const short = bal != null && bal < 0;
        return (
            // ref — 상단 '자금 부족 시작' 에서 이 칸으로 스크롤하기 위한 앵커.
            // Stack 이 아니라 Box + gap 을 쓴다 — MUI Stack 의 spacing 은 자식에게 margin-top 을 직접 걸어
            // 합계 줄의 'mt:auto'(칸 바닥에 붙이기)를 덮어써 칸마다 높이가 제각각이 된다.
            <Box ref={el => { dayRefs.current[key] = el; }}
                sx={{ display: "flex", flexDirection: "column", gap: 0.4, mt: 0.3, flex: 1, minHeight: 0 }}>
                {SECTIONS.map(sec => {
                    const list = items.filter(it => bucketOf(it) === sec.key);
                    if (list.length === 0) return null;   // 없는 방향의 머리말로 칸을 낭비하지 않는다
                    return (
                        <Box key={sec.key} sx={{ minWidth: 0 }}>
                            {/* 출금홀딩 머리말은 출금과 같은 빨강이라(홀딩 상태색) 한 톤 낮춰 둔다 —
                                같은 계열로 묶이되 확정 출금이 먼저 읽혀야 그 날 나갈 돈이 안 헷갈린다. */}
                            <Typography sx={{
                                fontSize: 9.5, fontWeight: 800, color: sec.color,
                                opacity: sec.key === "HOLD" ? 0.75 : 1,
                                letterSpacing: 0.2, px: 0.6, mb: 0.1,
                            }}>
                                {sec.label}
                            </Typography>
                            {list.map(it => renderItem(it, sec.text))}
                        </Box>
                    );
                })}

                {/* 일간 요약 — 고정비 달력과 같은 모양(왼쪽 라벨, 오른쪽 금액)으로 아래 순서를 쌓는다.
                      합계 (N건)   그 날 순액            ← 입금 − 출금
                      입금 / 출금  방향별 총액           ← 순액 하나로는 오간 규모를 알 수 없다
                                   고정비는 그 안의 일부라 라벨 옆 괄호로만 적는다(줄을 나누면 더해 버리게 된다)
                      홀딩         보류액                ← 홀딩이 있는 날만. 위 숫자 어디에도 안 들어간 금액
                      잔액         그 날 마감 예상 잔액  ← 항상 마지막 줄
                    <b>줄 수는 칸마다 같아야 한다</b> — 다르면 옆 칸과 눈금이 어긋나 가로 비교가 안 된다.
                    그래서 <b>항목이 하나도 없는 날도</b> 합계·입금·출금을 0원으로 적는다(빈 칸이 아니라
                    '그 날은 오간 돈이 없다'는 정보다). 네 줄이 모든 칸에 항상 같은 자리에 놓인다.
                    홀딩 줄만 예외로 있는 날에만 끼운다 — 항상 자리를 잡으면 대부분의 칸에서 0원 줄이
                    한 줄 더 늘어 항목 적을 자리를 먹는데, 홀딩이 걸린 날은 드물어서 그 대가가 크다.
                    (요약 덩어리는 mt:auto 로 칸 바닥에 붙으므로 늘어난 줄은 위로만 자란다.)
                    mx/mb 음수 여백은 셀 패딩(BaseCalendar 의 p:0.6)을 상쇄해 띠가 칸 끝까지 닿게 하는 것. */}
                <Box sx={{
                    mt: "auto", mx: -0.6, mb: -0.6, px: 0.8, py: 0.35,
                    borderTop: `0.5px solid ${short ? "#fecaca" : "#e2e8f0"}`,
                }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
                        <Typography sx={{
                            fontSize: 10, fontWeight: 700, color: "#94a3b8",
                            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                        }}>
                            합계 ({items.length}건)
                        </Typography>
                        <Typography sx={{
                            fontSize: 10.5, fontWeight: 800, color: "#0f172a",
                            whiteSpace: "nowrap", flexShrink: 0,
                        }}>
                            {fmtKRWFull(t.in - t.out)}
                        </Typography>
                    </Box>

                    {[
                        { key: "in",  label: "입금", sign: "+", amount: t.in,  fixed: t.inFixed,  color: KIND.INCOME.color },
                        { key: "out", label: "출금", sign: "−", amount: t.out, fixed: t.outFixed, color: KIND.EXPENSE.color },
                    ].map(r => (
                        <Box key={r.key}
                            title={r.fixed > 0 ? `${r.label} ${fmtKRWFull(r.amount)} (그중 고정비 ${fmtKRWFull(r.fixed)})` : undefined}
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, minWidth: 0 }}>
                            <Typography sx={{
                                fontSize: 9.5, fontWeight: 700, color: r.color,
                                minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                {r.label}
                                {r.fixed > 0 && (
                                    <Box component="span" sx={{ color: KIND.FIXED.color, fontWeight: 600 }}>
                                        {" "}(고정비 {fmtKRWFull(r.fixed)})
                                    </Box>
                                )}
                            </Typography>
                            <Typography sx={{
                                fontSize: 9.5, fontWeight: 700, color: "#475569",
                                whiteSpace: "nowrap", flexShrink: 0,
                            }}>
                                {r.sign}{fmtKRWFull(r.amount)}
                            </Typography>
                        </Box>
                    ))}

                    {/* 홀딩 — 금액에 취소선을 긋는다. "나갔으면 이만큼이었을 돈인데 지금은 안 센다"를
                        한 줄로 말해야 위의 출금·순액과 안 맞는다는 오해가 안 생긴다(항목 줄도 같은 취소선). */}
                    {t.holdCount > 0 && (
                        <Box title={`홀딩 ${t.holdCount}건 ${fmtKRWFull(t.hold)} — 보류 중이라 출금·순액·잔액에 넣지 않습니다`}
                            sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, minWidth: 0 }}>
                            <Typography sx={{
                                fontSize: 9.5, fontWeight: 700, color: KIND.HOLD.color, opacity: 0.75,
                                minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                                홀딩 {t.holdCount}건
                            </Typography>
                            <Typography sx={{
                                fontSize: 9.5, fontWeight: 700, color: KIND.HOLD.color, opacity: 0.75,
                                textDecoration: "line-through", whiteSpace: "nowrap", flexShrink: 0,
                            }}>
                                −{fmtKRWFull(t.hold)}
                            </Typography>
                        </Box>
                    )}

                    {bal != null && (
                        <Box sx={{
                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5,
                            mt: 0.2, pt: 0.2, borderTop: "0.5px dashed #e2e8f0",
                        }}>
                            <Typography sx={{
                                fontSize: 10, fontWeight: 700,
                                color: short ? "#dc2626" : "#94a3b8", whiteSpace: "nowrap",
                            }}>
                                잔액
                            </Typography>
                            <Typography sx={{
                                fontSize: 10.5, fontWeight: short ? 800 : 700,
                                color: short ? "#dc2626" : "#334155",
                                whiteSpace: "nowrap", flexShrink: 0,
                            }}>
                                {fmtKRWFull(bal)}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    /** 잔액이 마이너스인 날은 칸을 통째로 붉게 — 줄만으로는 '이 날 자금이 모자란다'가 안 읽힌다. */
    const getDayCellSx = (day, { isCurrentMonth }) => {
        const key = day.format(DATE);
        const bal = isCurrentMonth ? balanceByDate.get(key) : null;
        const short = bal != null && bal < 0;
        const flash = flashDate === key;
        if (!short && !flash) return { cursor: "default" };   // 조회 전용 화면이라 칸에 클릭 커서를 두지 않는다
        return {
            cursor: "default",
            // 아주 옅은 붉은 기 — 칸 안 배지(흰 바탕)가 묻히지 않을 만큼만.
            // 요약 줄에 따로 붉은 띠를 겹치면 그 부분만 두 번 칠해져 진해지므로 칸에만 칠한다.
            bgcolor: short ? "rgba(220,38,38,0.045)" : "transparent",
            "&:hover": { bgcolor: short ? "rgba(220,38,38,0.075)" : "#f8fafc" },
            ...(flash && {
                // '자금 부족 시작' 으로 막 이동한 칸 — 어디로 왔는지 잠깐 알려준다(2.6초 후 해제)
                outline: "2px solid #dc2626",
                outlineOffset: "-2px",
            }),
        };
    };

    /**
     * 주 사이 구분 띠 — 숫자는 넣지 않는다.
     *
     * <p>주간 합계·주말 잔액은 뺐다. 날짜 칸마다 합계와 잔액이 이미 있고 잔액은 누적이라 그 주 마지막
     * 칸의 잔액이 곧 주말 잔액이다. 다만 띠 자체는 남긴다 — 이게 없으면 6주가 한 덩어리로 붙어
     * 어디까지가 한 주인지 눈으로 끊기지 않는다.
     */
    const renderWeekFooter = () => (
        // 칸 배경(흰색·부족일 옅은 붉은색)보다 확실히 진해야 '주가 끊긴다'가 읽힌다.
        // 옅은 회색(#f8fafc)은 칸 안 요약 띠와 색이 비슷해 구분선 노릇을 못 했다.
        <Box sx={{
            height: 22, bgcolor: "#e2e8f0",
            borderTop: "1px solid #cbd5e1", borderBottom: "1px solid #cbd5e1",
        }} />
    );

    const item = detail?.item;

    /** 상세 팝오버 하단 안내 — 이 화면은 조회 전용이라 '어디서 고치는지'를 항상 알려준다. */
    const editHint = !item ? ""
        : item.preview     ? "아직 이번 달 자금계획으로 생성되지 않은 고정비입니다. 고정비 템플릿은 [고정비 달력]에서 관리합니다."
        : item.source === "APPROVAL"   ? "결재 연동 예정 건입니다. [결재 연동 내역]에서 처리합니다."
        : item.source === "RECEIVABLE" ? "미수금 예정 건입니다. [미수금 관리]에서 수금건을 등록하면 반영됩니다."
        : item.status === "COMPLETED"  ? "완료된 실적입니다. 되돌리기는 [현금 흐름표]에서 합니다."
        : "이 달력은 조회 전용입니다. 금액·예정일 수정과 상태 변경(긴급·홀딩 등)은 [현금 흐름표]에서 합니다.";

    return (
        <Box sx={{ pb: 10 }}>
            {/* ── 필터 바 — 상태·구분 칩. 달력은 셀이 좁아 표처럼 열을 못 늘리므로,
                   "무엇을 볼지"는 전부 여기서 결정하고 셀은 결과만 그린다. ── */}
            <Box sx={{
                display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.2,
                p: 1.2, mb: 1.5, border: "1px solid #e2e8f0", borderRadius: 1.5, bgcolor: "#fbfcfe",
            }}>
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#64748b" }}>상태</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                    {STATUS_CHIPS.map(o => {
                        const on = statusFilter.includes(o.value);
                        return (
                            <Chip key={o.value} label={o.label} size="small"
                                onClick={() => toggle(setStatusFilter, statusFilter, o.value)}
                                sx={{
                                    cursor: "pointer", fontSize: 12, fontWeight: 700, height: 26,
                                    color: on ? o.color : "#94a3b8",
                                    bgcolor: on ? o.bg : "transparent",
                                    "&:hover": { bgcolor: on ? o.bg : "#f1f5f9" },
                                }} />
                        );
                    })}
                </Box>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                {/* 구분 칩은 그대로 섹션 머리말 색의 범례 역할도 한다.
                    '출금홀딩'은 상태 칩의 '홀딩'과 같은 건을 가리키고 둘은 AND 로 걸린다 —
                    한쪽만 꺼도 홀딩은 사라진다(상태 칩=무슨 상태를, 구분 칩=어느 칸에 놓고 볼지). */}
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: "#64748b" }}>구분</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
                    {KIND_CHIPS.map(k => {
                        const on = kindFilter.includes(k.key);
                        return (
                            <Chip key={k.key} label={k.label} size="small"
                                onClick={() => toggle(setKindFilter, kindFilter, k.key)}
                                sx={{
                                    cursor: "pointer", fontSize: 12, fontWeight: 700, height: 26,
                                    color: on ? k.color : "#94a3b8",
                                    bgcolor: on ? k.bg : "transparent",
                                    "&:hover": { bgcolor: on ? k.bg : "#f1f5f9" },
                                }} />
                        );
                    })}
                </Box>

                <Box sx={{ flex: 1 }} />

            </Box>

            {/* ── 잔액 줄 — 기준잔액 → 월말 예상 잔액 → 부족(빵꾸) 시점. 배경 없이 한 줄로. ──
                [주의] flexWrap 을 켜면 안 된다. 아래처럼 라벨·값을 한 줄에 늘어놓는 구조에서
                'flex:1 스페이서 + flexWrap' 조합은 스페이서가 첫 줄의 남는 폭을 통째로 먹어
                뒤 항목이 전부 다음 줄로 밀린다(실제로 그렇게 3~4줄로 접혀 있었다).
                좁은 화면에서는 줄을 접는 대신 가로 스크롤로 흘린다. */}
            <Box sx={{
                display: "flex", alignItems: "baseline", gap: 1, px: 0.5, py: 0.8, mb: 1,
                borderBottom: "1px solid #f1f5f9", overflowX: "auto", whiteSpace: "nowrap",
            }}>
                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#94a3b8", flexShrink: 0 }}>
                    기준 잔액 {base?.baseDate ? `(${dayjs(base.baseDate).format("M/D")} 일마감)` : "(미설정)"}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#0f172a", flexShrink: 0 }}>
                    {fmtKRWFull(base?.amount ?? 0)}
                </Typography>

                <Typography sx={{ fontSize: 12, color: "#cbd5e1", flexShrink: 0 }}>→</Typography>

                <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#94a3b8", flexShrink: 0 }}>
                    {anchor.format("M월")} 말 예상
                </Typography>
                <Typography sx={{
                    fontSize: 14, fontWeight: 800, flexShrink: 0,
                    color: (endBalance ?? 0) < 0 ? "#dc2626" : "#0f172a",
                }}>
                    {endBalance == null ? "—" : fmtKRWFull(endBalance)}
                </Typography>

                {shortfallDate && (
                    // 누르면 그 날짜 칸으로 이동 — 배너가 짚는 날을 눈으로 찾게 두지 않는다
                    <Box onClick={jumpToShortfall} title="해당 날짜로 이동"
                        sx={{
                            display: "flex", alignItems: "baseline", gap: 0.7, flexShrink: 0,
                            cursor: "pointer", px: 0.8, py: 0.2, ml: 0.5, borderRadius: 1,
                            "&:hover": { bgcolor: "#fef2f2" },
                        }}>
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#dc2626" }}>
                            자금 부족 시작
                        </Typography>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#dc2626" }}>
                            {dayjs(shortfallDate).format("M월 D일")} · {fmtKRWFull(balanceByDate.get(shortfallDate))}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: "#dc2626" }}>→</Typography>
                    </Box>
                )}

                <Box sx={{ flex: 1, minWidth: 8 }} />
            </Box>

            <BaseCalendar
                anchor={anchor}
                onAnchorChange={setAnchor}
                view="month"
                supportedViews={["month"]}
                loading={loading}
                onRefresh={load}
                cellHeight={132}
                renderDayBadges={renderDayBadges}
                renderWeekFooter={renderWeekFooter}
                getDayCellSx={getDayCellSx}
                toolbarLeft={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, ml: 1, flexWrap: "wrap" }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: KIND.EXPENSE.color }}>
                            출금 −{fmtKRWFull(month.out)}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: KIND.INCOME.color }}>
                            입금 +{fmtKRWFull(month.in)}
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: monthNet < 0 ? "#dc2626" : "#0f172a" }}>
                            순액 {monthNet < 0 ? "−" : "+"}{fmtKRWFull(Math.abs(monthNet))}
                        </Typography>
                        {/* 달 단위에서는 '아직 안 나간 돈'이 따로 필요하다 — 자금 계획은 그 숫자로 세운다.
                            (날짜 칸은 그 날 오간 돈만 보면 되므로 예정/완료를 나누지 않는다.) */}
                        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "#94a3b8" }}>
                            | 이 중 예정 출금 −{fmtKRWFull(month.outPlan)} · 입금 +{fmtKRWFull(month.inPlan)}
                        </Typography>
                        {/* 월 홀딩 — 출금 합계 밖의 돈이라 색·톤을 칸 안 '출금홀딩'과 맞춰 같은 것임을 알린다. */}
                        {month.holdCount > 0 && (
                            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: KIND.HOLD.color, opacity: 0.75 }}>
                                | 출금홀딩 {month.holdCount}건 {fmtKRWFull(month.hold)}
                            </Typography>
                        )}
                    </Box>
                }
                toolbarRight={
                    <Stack direction="row" spacing={1} alignItems="center">
                        <SearchField value={search} onChange={setSearch}
                            placeholder="항목·거래처·분류 검색" width={200} />
                    </Stack>
                }
            />

            {/* ── 항목 상세 — 조회 전용. 수정은 각자의 관리 화면에서 한다 ── */}
            <Popover
                open={Boolean(detail)}
                anchorEl={detail?.anchorEl}
                onClose={() => setDetail(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { borderRadius: 1.5, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", width: 320 } }}
            >
                {item && (
                    <Box sx={{ p: 2 }}>
                        <Stack direction="row" alignItems="center" spacing={0.8} sx={{ mb: 0.8, flexWrap: "wrap" }}>
                            <Chip size="small" label={statusInfo(item.status).label}
                                sx={{
                                    height: 22, fontSize: 11, fontWeight: 800,
                                    color: statusInfo(item.status).color, bgcolor: statusInfo(item.status).bg,
                                }} />
                            {item.fixed && (
                                <Chip size="small" label="고정비"
                                    sx={{ height: 22, fontSize: 11, fontWeight: 700, color: "#7c3aed", bgcolor: "#f5f3ff" }} />
                            )}
                            {item.preview && (
                                <Chip size="small" label="미생성"
                                    sx={{ height: 22, fontSize: 11, fontWeight: 700, color: "#64748b", bgcolor: "#f1f5f9" }} />
                            )}
                            {item.overdue && item.status !== "COMPLETED" && (
                                <Chip size="small" label="지연"
                                    sx={{ height: 22, fontSize: 11, fontWeight: 700, color: "#b45309", bgcolor: "#fffbeb" }} />
                            )}
                        </Stack>

                        <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: "#0f172a", mb: 0.3 }}>
                            {item.title}
                        </Typography>
                        <Typography sx={{
                            fontSize: 17, fontWeight: 800, mb: 1,
                            color: item.flowType === "INCOME" ? KIND.INCOME.color : KIND[bucketOf(item)].color,
                        }}>
                            {item.flowType === "INCOME" ? "+" : "−"}{fmtKRWFull(item.amount)}
                        </Typography>

                        <Stack spacing={0.4} sx={{ mb: 1.2 }}>
                            {[
                                ["예정일", dayjs(item.date).format("YYYY.MM.DD (ddd)")],
                                ["잔액",   balanceByDate.get(item.date) != null
                                            ? `${fmtKRWFull(balanceByDate.get(item.date))} (그 날 마감 예상)` : null],
                                ["출처",   item.sourceLabel],
                                ["거래처", item.vendorName],
                                ["분류",   item.category],
                                ["팀",     item.deptName],
                                ["담당자", item.authorName],
                                ["비고",   item.note],
                            ].filter(([, v]) => v).map(([k, v]) => (
                                <Box key={k} sx={{ display: "flex", gap: 1 }}>
                                    <Typography sx={{ fontSize: 12, color: "#94a3b8", width: 52, flexShrink: 0 }}>{k}</Typography>
                                    <Typography sx={{ fontSize: 12, color: "#475569", flex: 1, wordBreak: "break-all" }}>{v}</Typography>
                                </Box>
                            ))}
                        </Stack>

                        <Divider sx={{ mb: 1 }} />
                        <Typography sx={{ fontSize: 11.5, color: "#94a3b8", lineHeight: 1.6 }}>
                            {editHint}
                        </Typography>
                    </Box>
                )}
            </Popover>
        </Box>
    );
}
